import { useFormik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';
import { useUpdateChannelGuide } from '~/api/channel-guides';
import { ChannelSelect } from '../components/ChannelSelect';
import type { ChannelGuideSettingsProps } from '../components/settings-form';

const validationSchema = object({
  defaultChannelId: number().nullable().optional(),
});

export const useSettingsForm = ({ channelGuide, channels }: ChannelGuideSettingsProps) => {
  const { t } = useTranslation();
  const [updateChannelGuide] = useUpdateChannelGuide();

  const initialValues = useMemo(
    () => ({
      defaultChannelId: channelGuide.defaultChannel?.id ?? '',
    }),
    [channelGuide],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values) => {
      await updateChannelGuide({
        variables: {
          input: { id: channelGuide.id, patch: validationSchema.cast(values) },
        },
      });
    },
    validateOnBlur: true,
    validationSchema,
  });

  const defaultChannel = useMemo(
    () => channels.find(({ id }) => id === formik.values.defaultChannelId) ?? null,
    [channels, formik.values.defaultChannelId],
  );

  const availableChannels = useMemo(() => channelGuide.channels, [channelGuide]);

  const generalSettings = useMemo(() => {
    return [
      {
        heading: t('pages.channel_guides.detail.settings.default_channel.heading'),
        subHeading: t('pages.channel_guides.detail.settings.default_channel.subheading'),
        dataField: (
          <ChannelSelect
            disabled={availableChannels.length === 0}
            onChange={(channel) => formik.setFieldValue('defaultChannelId', channel?.id ?? null)}
            options={availableChannels}
            value={defaultChannel}
          />
        ),
      },
    ];
  }, [availableChannels, defaultChannel, formik, t]);

  return {
    formik,
    generalSettings,
  };
};
